import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import type { Outcome } from '../../../../api/strategy-client/strategy-client.type';
import { FilterGroup } from '../../../../components/filters/filter-panel.type';
import { getRiskAnalysisTaskTypeValue } from '../risk-analysis-task-type.helpers';
import { RiskAnalysisSection } from '../risk-analysis.client.type';
import type { RiskAnalysisInitiative, RiskAnalysisPullRequest, RiskAnalysisTask } from '../risk-analysis.client.type';
import { calculateCompletionPercentage, getRiskAnalysisCreatorValue, getRiskCategory } from '../risk-analysis.helpers';

/**
 * Custom hook that filters data based on various filter criteria
 *
 * @param {RiskAnalysisTask[] | undefined} tasks - Array of RiskAnalysisTask objects to filter
 * @param {RiskAnalysisInitiative[] | undefined} initiatives - Array of RiskAnalysisInitiative objects to filter
 * @param {RiskAnalysisTask[] | undefined} epics - Array of RiskAnalysisTask objects representing epics to filter
 * @param {RiskAnalysisPullRequest[] | undefined} pullRequests - Array of RiskAnalysisPullRequest objects to filter
 * @param {Outcome[] | undefined} outcomes - Array of Outcome objects to filter
 * @param {Object} filters - Object containing all filter criteria
 * @returns {Object} Object containing filtered data for tasks, initiatives, epics, pull requests and outcomes
 */
const useFilteredData = (
  tasks: RiskAnalysisTask[] | undefined,
  initiatives: RiskAnalysisInitiative[] | undefined,
  epics: RiskAnalysisTask[] | undefined,
  pullRequests: RiskAnalysisPullRequest[] | undefined,
  outcomes: Outcome[] | undefined,
  filters: {
    creatorFilters: string[];
    projectFilters: string[];
    typeFilters: string[];
    epicFilters: string[];
    initiativeFilters: string[];
    riskCategoryFilters: string[];
    bloomfilterStageFilters: string[];
    includeDone: boolean;
    activeInitiativeFilter: string;
    initiativeRiskCategoryFilters: string[];
    includeDoneInitiatives: boolean;
    epicProjectFilters: string[];
    epicInitiativeFilters: string[];
    epicRiskCategoryFilters: string[];
    includeDoneEpics: boolean;
    prRepositoryFilters: string[];
    prStateFilters: string[];
    prRiskCategoryFilters: string[];
    objectiveStatusFilters: string[];
    objectiveRiskCategoryFilters: string[];
  },
): {
  filteredTasks: RiskAnalysisTask[];
  filteredInitiatives: RiskAnalysisInitiative[];
  filteredEpics: RiskAnalysisTask[];
  filteredPullRequests: RiskAnalysisPullRequest[];
  filteredOutcomes: Outcome[];
} => {
  const filteredTasks = useMemo(
    () =>
      (tasks ?? []).filter((task) => {
        // If no filter values are selected, show all tasks (consistent "empty = show all" pattern).
        // Reporter-less tasks match under the 'No creator' sentinel the options
        // builder emits for them — without it they could never match a seeded
        // (non-empty) selection and would silently vanish from every view.
        const creatorMatch =
          filters.creatorFilters.length === 0 || filters.creatorFilters.includes(getRiskAnalysisCreatorValue(task));

        const projectMatch = filters.projectFilters.length === 0 || filters.projectFilters.includes(task.project.name);

        // Type filter values are the lowercased raw_type keys the Types filter
        // options carry, so matching must run through the same key derivation.
        const typeMatch =
          filters.typeFilters.length === 0 || filters.typeFilters.includes(getRiskAnalysisTaskTypeValue(task));

        // If no epic filters are selected, show all tasks. Otherwise, only show tasks with matching epics
        const epicMatch =
          filters.epicFilters.length === 0 || (task.epic?.name && filters.epicFilters.includes(task.epic.name));

        // If no initiative filters are selected, show all tasks. Otherwise, only show tasks with matching initiatives
        const initiativeMatch =
          filters.initiativeFilters.length === 0 ||
          (task.initiatives &&
            task.initiatives.some((initiative) => filters.initiativeFilters.includes(initiative.id)));

        const riskCategoryMatch =
          filters.riskCategoryFilters.length === 0 ||
          filters.riskCategoryFilters.includes(getRiskCategory(task.flora_annotations?.risk_score).label);

        const bloomfilterStageMatch =
          filters.bloomfilterStageFilters.length === 0 ||
          filters.bloomfilterStageFilters.includes(task.bloomfilter_stage ?? 'Unmapped');

        const doneMatch = filters.includeDone ? true : task.done_date === null;

        return (
          creatorMatch &&
          projectMatch &&
          typeMatch &&
          epicMatch &&
          initiativeMatch &&
          riskCategoryMatch &&
          bloomfilterStageMatch &&
          doneMatch
        );
      }),
    [tasks, filters],
  );

  const filteredInitiatives = useMemo(() => {
    return (initiatives ?? []).filter((initiative) => {
      const now = new Date();
      const startDate = initiative.start_date ? new Date(initiative.start_date) : null;
      const endDate = initiative.end_date ? new Date(initiative.end_date) : null;

      const isActive = (startDate && (!endDate || endDate > now) && startDate <= now) || initiative.is_active;
      const statusMatch = filters.activeInitiativeFilter === 'active' ? isActive : !isActive;

      const riskCategoryMatch =
        filters.initiativeRiskCategoryFilters.length === 0 ||
        filters.initiativeRiskCategoryFilters.includes(getRiskCategory(initiative.flora_annotations?.risk_score).label);

      const doneMatch = filters.includeDoneInitiatives ? true : initiative.completion_percentage < 100;

      return statusMatch && riskCategoryMatch && doneMatch;
    });
  }, [
    initiatives,
    filters.activeInitiativeFilter,
    filters.initiativeRiskCategoryFilters,
    filters.includeDoneInitiatives,
  ]);

  const filteredEpics = useMemo(() => {
    return (epics ?? []).filter((epic) => {
      // If no project filters are selected, show all epics. Otherwise, only show epics with matching projects
      const projectMatch =
        filters.epicProjectFilters.length === 0 || filters.epicProjectFilters.includes(epic.project.name);

      // If no initiative filters are selected, show all epics. Otherwise, only show epics with matching initiatives
      const initiativeMatch =
        filters.epicInitiativeFilters.length === 0 ||
        (epic.initiatives &&
          epic.initiatives.some((initiative) => filters.epicInitiativeFilters.includes(initiative.id)));

      // If no risk category filters are selected, show all epics. Otherwise, only show epics with matching risk categories
      const riskCategoryMatch =
        filters.epicRiskCategoryFilters.length === 0 ||
        filters.epicRiskCategoryFilters.includes(getRiskCategory(epic.flora_annotations?.risk_score).label);

      const doneMatch = filters.includeDoneEpics ? true : calculateCompletionPercentage(epic) < 100;

      return projectMatch && initiativeMatch && riskCategoryMatch && doneMatch;
    });
  }, [
    epics,
    filters.epicProjectFilters,
    filters.epicInitiativeFilters,
    filters.epicRiskCategoryFilters,
    filters.includeDoneEpics,
  ]);

  const filteredPullRequests = useMemo(() => {
    return (pullRequests ?? []).filter((pr) => {
      // If no repositories are selected, show no pull requests
      if (filters.prRepositoryFilters.length === 0) {
        return false;
      }

      // If no risk categories are selected, show no pull requests
      if (filters.prRiskCategoryFilters.length === 0) {
        return false;
      }

      const repositoryMatch = filters.prRepositoryFilters.includes(pr.repository.name);
      const stateMatch = filters.prStateFilters.includes(pr.state);
      const riskCategoryMatch = filters.prRiskCategoryFilters.includes(
        getRiskCategory(pr.flora_annotations?.risk_score).label,
      );

      return repositoryMatch && stateMatch && riskCategoryMatch;
    });
  }, [pullRequests, filters.prRepositoryFilters, filters.prStateFilters, filters.prRiskCategoryFilters]);

  const filteredOutcomes = useMemo(() => {
    return (outcomes ?? []).filter((outcome) => {
      // Filter by task status
      const outcomeStatus = outcome.status?.by_tasks?.status || 'unknown';
      const matchesTaskStatus =
        filters.objectiveStatusFilters.length === 0 || filters.objectiveStatusFilters.includes(outcomeStatus);

      // Filter by risk category
      const riskCategory = getRiskCategory(outcome.flora_annotations?.risk_score).label;
      const matchesRiskCategory =
        filters.objectiveRiskCategoryFilters.length === 0 ||
        filters.objectiveRiskCategoryFilters.includes(riskCategory);

      return matchesTaskStatus && matchesRiskCategory;
    });
  }, [outcomes, filters.objectiveStatusFilters, filters.objectiveRiskCategoryFilters]);

  return {
    filteredTasks,
    filteredInitiatives,
    filteredEpics,
    filteredPullRequests,
    filteredOutcomes,
  };
};

/**
 * Generates filter groups based on the selected section
 *
 * @param {RiskAnalysisSection} selectedSection - The section to generate filters for
 * @param {Object} params - Object containing all filter options and filters
 * @returns {FilterGroup[]} Array of filter groups
 */
const getFilterGroups = (
  selectedSection: RiskAnalysisSection,
  {
    projectOptions,
    epicProjectOptions,
    taskTypes,
    riskCategories,
    epicRiskCategories,
    creators,
    initiativesFilterOptions,
    epicsFilterOptions,
    initiativeFilterOptionsForEpic,
    repositoryOptions,
    bloomfilterStageOptions,
    filters,
    activeTab,
  }: {
    projectOptions: { value: string; label: string }[];
    epicProjectOptions: { value: string; label: string }[];
    taskTypes: { value: string; label: string }[];
    riskCategories: { value: string; label: string }[];
    epicRiskCategories: { value: string; label: string }[];
    creators: { value: string; label: string }[];
    initiativesFilterOptions: { value: string; label: string }[];
    epicsFilterOptions: { value: string; label: string }[];
    initiativeFilterOptionsForEpic: { value: string; label: string }[];
    repositoryOptions: { value: string; label: string }[];
    activeTab?: 'table' | 'flora';
    filters: {
      projectFilters: string[];
      typeFilters: string[];
      riskCategoryFilters: string[];
      bloomfilterStageFilters: string[];
      creatorFilters: string[];
      initiativeFilters: string[];
      epicFilters: string[];
      includeDone: boolean;
      activeInitiativeFilter: string;
      initiativeRiskCategoryFilters: string[];
      includeDoneInitiatives: boolean;
      epicProjectFilters: string[];
      epicInitiativeFilters: string[];
      epicRiskCategoryFilters: string[];
      includeDoneEpics: boolean;
      prRepositoryFilters: string[];
      prStateFilters: string[];
      prRiskCategoryFilters: string[];
      floraInsightTypeFilters: string[];
      floraSeverityFilters: string[];
      floraStatusFilters: string[];
      floraViewFilter: string;
      objectiveStatusFilters: string[];
      objectiveRiskCategoryFilters: string[];
      setProjectFilters: (filters: string[]) => void;
      setTypeFilters: (filters: string[]) => void;
      setRiskCategoryFilters: (filters: string[]) => void;
      setBloomfilterStageFilters: (filters: string[]) => void;
      setCreatorFilters: (filters: string[]) => void;
      setInitiativeFilters: (filters: string[]) => void;
      setEpicFilters: (filters: string[]) => void;
      setIncludeDone: (includeDone: boolean) => void;
      setActiveInitiativeFilter: (filter: string) => void;
      setInitiativeRiskCategoryFilters: (filters: string[]) => void;
      setIncludeDoneInitiatives: (value: boolean) => void;
      setEpicProjectFilters: (filters: string[]) => void;
      setEpicInitiativeFilters: (filters: string[]) => void;
      setEpicRiskCategoryFilters: (filters: string[]) => void;
      setIncludeDoneEpics: (value: boolean) => void;
      setPrRepositoryFilters: (filters: string[]) => void;
      setPrStateFilters: (filters: string[]) => void;
      setPrRiskCategoryFilters: (filters: string[]) => void;
      setFloraInsightTypeFilters: (filters: string[]) => void;
      setFloraSeverityFilters: (filters: string[]) => void;
      setFloraStatusFilters: (filters: string[]) => void;
      setFloraViewFilter: (filter: string) => void;
      setObjectiveStatusFilters: (filters: string[]) => void;
      setObjectiveRiskCategoryFilters: (filters: string[]) => void;
    };
    bloomfilterStageOptions: { value: string; label: string }[];
  },
  t: TFunction,
): FilterGroup[] => {
  const taskFilterGroups = [
    {
      name: t('common.projects'),
      icon: 'house_siding',
      options: projectOptions,
      selectedValues: filters.projectFilters,
      onSelectionChange: filters.setProjectFilters,
      searchable: true,
    },
    {
      name: 'Types',
      icon: 'type_specimen',
      options: taskTypes,
      selectedValues: filters.typeFilters,
      onSelectionChange: filters.setTypeFilters,
    },
    {
      name: 'Bloomfilter Status',
      icon: 'account_tree',
      options: bloomfilterStageOptions,
      selectedValues: filters.bloomfilterStageFilters,
      onSelectionChange: filters.setBloomfilterStageFilters,
    },
    {
      name: 'Risk Categories',
      icon: 'warning_amber',
      options: riskCategories,
      selectedValues: filters.riskCategoryFilters,
      onSelectionChange: filters.setRiskCategoryFilters,
    },
    {
      name: 'Creators',
      icon: 'person',
      options: creators,
      selectedValues: filters.creatorFilters,
      onSelectionChange: filters.setCreatorFilters,
    },
    {
      name: t('common.initiatives'),
      icon: 'ads_click',
      options: initiativesFilterOptions,
      selectedValues: filters.initiativeFilters,
      onSelectionChange: filters.setInitiativeFilters,
      optional: true,
    },
    {
      name: t('common.epics'),
      icon: 'electric_bolt',
      options: epicsFilterOptions,
      selectedValues: filters.epicFilters,
      onSelectionChange: filters.setEpicFilters,
      optional: true,
    },
    {
      name: 'General',
      icon: 'settings',
      showAll: false,
      options: [
        {
          value: 'include_done_tasks',
          label: t('common.include_done_tasks'),
        },
      ],
      selectedValues: filters.includeDone ? ['include_done_tasks'] : [],
      onSelectionChange: (value: string[]) => {
        filters.setIncludeDone(value.includes('include_done_tasks'));
      },
    },
  ];

  const initiativeFilterGroups = [
    {
      name: 'Status',
      icon: 'schedule',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
      ],
      selectedValues: [filters.activeInitiativeFilter],
      onSelectionChange: (values: string[]) => {
        filters.setActiveInitiativeFilter(values.length ? values.join(',') : 'active');
      },
      showAll: false,
    },
    {
      name: 'Risk Categories',
      icon: 'warning_amber',
      options: riskCategories,
      selectedValues: filters.initiativeRiskCategoryFilters,
      onSelectionChange: filters.setInitiativeRiskCategoryFilters,
    },
    {
      name: 'General',
      icon: 'settings',
      showAll: false,
      options: [
        {
          value: 'include_done_initiatives',
          label: t('common.include_done_initiatives'),
        },
      ],
      selectedValues: filters.includeDoneInitiatives ? ['include_done_initiatives'] : [],
      onSelectionChange: (value: string[]) => {
        filters.setIncludeDoneInitiatives(value.includes('include_done_initiatives'));
      },
    },
  ];

  const epicFilterGroups = [
    {
      name: 'Projects',
      icon: 'house_siding',
      options: epicProjectOptions,
      selectedValues: filters.epicProjectFilters,
      onSelectionChange: filters.setEpicProjectFilters,
      searchable: true,
    },
    {
      name: t('common.initiatives'),
      icon: 'ads_click',
      options: initiativeFilterOptionsForEpic,
      selectedValues: filters.epicInitiativeFilters,
      onSelectionChange: filters.setEpicInitiativeFilters,
      optional: true,
    },
    {
      name: 'Risk Categories',
      icon: 'warning_amber',
      options: epicRiskCategories,
      selectedValues: filters.epicRiskCategoryFilters,
      onSelectionChange: filters.setEpicRiskCategoryFilters,
    },
    {
      name: 'General',
      icon: 'settings',
      showAll: false,
      options: [
        {
          value: 'include_done_epics',
          label: t('common.include_done_epics'),
        },
      ],
      selectedValues: filters.includeDoneEpics ? ['include_done_epics'] : [],
      onSelectionChange: (value: string[]) => {
        filters.setIncludeDoneEpics(value.includes('include_done_epics'));
      },
    },
  ];

  const pullRequestFilterGroups = [
    {
      name: 'Repositories',
      icon: 'code',
      options: repositoryOptions,
      selectedValues: filters.prRepositoryFilters,
      onSelectionChange: filters.setPrRepositoryFilters,
    },
    {
      name: 'State',
      icon: 'check_circle',
      options: [
        { value: 'open', label: 'Open' },
        { value: 'closed', label: 'Closed' },
      ],
      selectedValues: filters.prStateFilters,
      onSelectionChange: filters.setPrStateFilters,
    },
    {
      name: 'Risk Categories',
      icon: 'warning_amber',
      options: riskCategories,
      selectedValues: filters.prRiskCategoryFilters,
      onSelectionChange: filters.setPrRiskCategoryFilters,
    },
  ];

  const floraFilterGroups = [
    {
      name: 'Insight Type',
      icon: 'category',
      options: [
        { value: 'task', label: 'Tasks' },
        { value: 'epic', label: t('common.epics') },
        { value: 'initiative', label: 'Initiatives' },
        { value: 'pull_request', label: 'Pull Requests' },
      ],
      selectedValues: filters.floraInsightTypeFilters,
      onSelectionChange: filters.setFloraInsightTypeFilters,
      optional: true,
    },
    {
      name: 'Severity',
      icon: 'priority_high',
      options: [
        { value: 'high', label: 'High' },
        { value: 'medium', label: 'Medium' },
        { value: 'low', label: 'Low' },
      ],
      selectedValues: filters.floraSeverityFilters,
      onSelectionChange: filters.setFloraSeverityFilters,
      optional: true,
    },
    {
      name: 'Status',
      icon: 'check_circle',
      options: [
        { value: 'not_started', label: 'Not Started' },
        { value: 'in_progress', label: 'In Progress' },
        { value: 'completed', label: 'Completed' },
      ],
      selectedValues: filters.floraStatusFilters,
      onSelectionChange: filters.setFloraStatusFilters,
      optional: true,
    },
    {
      name: 'View',
      icon: 'visibility',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'dismissed', label: 'Dismissed' },
        { value: 'inactive', label: 'Inactive' },
      ],
      selectedValues: [filters.floraViewFilter],
      onSelectionChange: (values: string[]) => {
        filters.setFloraViewFilter(values.length ? values.join(',') : 'active');
      },
      showAll: false,
    },
  ];

  // Return Flora filters when Flora tab is active
  if (activeTab === 'flora') {
    return floraFilterGroups;
  }

  const objectiveFilterGroups = [
    {
      name: 'Status',
      icon: 'filter_tilt_shift',
      options: [
        { value: 'on_track', label: 'On Track' },
        { value: 'at_risk', label: 'At Risk' },
        { value: 'off_track', label: 'Off Track' },
        { value: 'unknown', label: 'Unknown' },
      ],
      selectedValues: filters.objectiveStatusFilters,
      onSelectionChange: filters.setObjectiveStatusFilters,
      showAllLabel: 'All Statuses',
    },
    {
      name: 'Risk Categories',
      icon: 'warning_amber',
      options: riskCategories,
      selectedValues: filters.objectiveRiskCategoryFilters,
      onSelectionChange: filters.setObjectiveRiskCategoryFilters,
    },
  ];

  switch (selectedSection) {
    case RiskAnalysisSection.TASKS:
      return taskFilterGroups;
    case RiskAnalysisSection.INITIATIVES:
      return initiativeFilterGroups;
    case RiskAnalysisSection.EPICS:
      return epicFilterGroups;
    case RiskAnalysisSection.PULL_REQUESTS:
      return pullRequestFilterGroups;
    case RiskAnalysisSection.OUTCOMES:
      return objectiveFilterGroups;
    default:
      return [];
  }
};

export { getFilterGroups, useFilteredData };
